// Smart API URL detection for localhost and IP access
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Get current host and replace port with 8004 for backend
  const host = typeof window !== 'undefined' ? window.location.hostname : '127.0.0.1';
  const apiUrl = `http://${host}:8004/api`;
  console.log('API URL:', apiUrl, 'Host:', host);
  return apiUrl;
};

const API_URL = getApiUrl();

const hubHeaders = () => {
  const token = new URLSearchParams(window.location.search).get('hub_token') || localStorage.getItem('hubToken') || '';
  if (token) localStorage.setItem('hubToken', token);
  return token ? { 'X-Hub-Token': token } : {};
};

const errorFromResponse = async (res, fallback) => {
  const text = await res.text();
  try {
    const data = JSON.parse(text);
    return data.error || data.detail || fallback;
  } catch (err) {
    return text || fallback;
  }
};

const api = {
  leaderboardRecordsUrl: `${API_URL}/leaderboard-records/`,
  dailyLetters: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.megaMonday) {
      params.set('mega_monday', '1');
    }
    if (options.pointsMode) {
      params.set('points_mode', '1');
    }
    const query = params.toString();
    const res = await fetch(`${API_URL}/daily-letters/${query ? `?${query}` : ''}`);
    if (!res.ok) throw new Error('Failed to fetch daily letters');
    return res.json();
  },

  checkWord: async (word) => {
    const res = await fetch(`${API_URL}/check-word/?word=${encodeURIComponent(word)}`);
    if (!res.ok) throw new Error('Failed to check word');
    return res.json();
  },

  validateGrid: async (grid) => {
    const res = await fetch(`${API_URL}/validate-grid/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ grid })
    });
    if (!res.ok) throw new Error(await errorFromResponse(res, 'Failed to validate grid'));
    return res.json();
  },

  startGame: async (playerName, options = {}) => {
    console.log('Starting game for player:', playerName, 'API URL:', API_URL);
    try {
      const res = await fetch(`${API_URL}/start-game/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...hubHeaders() },
        body: JSON.stringify({
          player_name: playerName,
          ...(options.megaMonday ? { mega_monday: true } : {}),
          ...(options.pointsMode ? { points_mode: true } : {})
        })
      });
      console.log('Start game response status:', res.status);

      if (!res.ok) {
        const errorText = await res.text();
        console.error('Start game error:', res.status, errorText);
        const error = new Error(`API Error ${res.status}: ${errorText}`);
        error.status = res.status;
        error.body = errorText;
        throw error;
      }

      const data = await res.json();
      console.log('Start game success:', data);
      return data;
    } catch (err) {
      console.error('Start game failed:', err.message, err);
      if (err.status) {
        throw err;
      }
      throw new Error(`Failed to start game: ${err.message}`);
    }
  },

  submitScore: async (playerName, grid, options = {}) => {
    const res = await fetch(`${API_URL}/submit-score/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...hubHeaders() },
      body: JSON.stringify({
        player_name: playerName,
        grid,
        ...(options.pointsMode ? { points_mode: true } : {})
      })
    });
    if (!res.ok) throw new Error(await errorFromResponse(res, 'Failed to submit score'));
    return res.json();
  },

  leaderboard: async (date, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ limit, offset });
    if (date) {
      params.set('date', date);
    }
    const res = await fetch(`${API_URL}/leaderboard/?${params}`);
    if (!res.ok) throw new Error('Failed to fetch leaderboard');
    return res.json();
  },

  historicalStats: async () => {
    const res = await fetch(`${API_URL}/historical-stats/`);
    if (!res.ok) throw new Error('Failed to fetch historical stats');
    return res.json();
  },

  playerBoard: async (playerName, date, requesterName) => {
    const params = new URLSearchParams({ player_name: playerName, date });
    if (requesterName) {
      params.set('requester_name', requesterName);
    }
    const res = await fetch(`${API_URL}/player-board/?${params}`);
    if (!res.ok) throw new Error(await errorFromResponse(res, 'Failed to fetch player board'));
    return res.json();
  },

  hasPlayedToday: async (playerName) => {
    const params = new URLSearchParams({ player_name: playerName });
    const res = await fetch(`${API_URL}/has-played-today/?${params}`);
    if (!res.ok) throw new Error('Failed to check if played today');
    return res.json();
  },

  todayWinner: async () => {
    const res = await fetch(`${API_URL}/today-winner/`);
    if (!res.ok) throw new Error('Failed to fetch today winner');
    return res.json();
  },

  yesterdayWinner: async () => {
    const res = await fetch(`${API_URL}/yesterday-winner/`);
    if (!res.ok) throw new Error('Failed to fetch yesterday winner');
    return res.json();
  },

  reserveName: async (playerName, password) => {
    const res = await fetch(`${API_URL}/reserve-name/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ player_name: playerName, password })
    });
    if (!res.ok) throw new Error('Failed to reserve name');
    return res.json();
  },

  verifyName: async (playerName, password) => {
    const res = await fetch(`${API_URL}/verify-name/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ player_name: playerName, password })
    });
    if (!res.ok) throw new Error('Failed to verify name');
    return res.json();
  }
};

export default api;
